// What each step runs on. The trigger decides it for the steps at the root (a
// signal, or a placed prospect order); a step that makes a new subject ("Create
// prospect order" on a signal) runs the steps chained under it on that. Which
// steps and agents may go where follows from it, as the API decides it.

import { createContext } from "react";
import { SIGNAL_TRIGGER, type WorkflowStepType, type WorkflowSubjectKind } from "../../../api/client.ts";
import { triggerDataOf } from "./graph.ts";
import {
  CHILD_SUBJECT,
  STEP_SUBJECTS,
  TRIGGER_ID,
  type ActionKind,
  type ConditionEdge,
  type StepData,
  type StepNode,
} from "./types.ts";

/** The kind of subject every connected step runs on, by node id (the trigger: its own). */
export function subjectsOf(nodes: StepNode[], edges: ConditionEdge[]): Map<string, WorkflowSubjectKind> {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const outgoing = new Map<string, string[]>();
  for (const e of edges) outgoing.set(e.source, [...(outgoing.get(e.source) ?? []), e.target]);
  const subjects = new Map<string, WorkflowSubjectKind>([
    [TRIGGER_ID, triggerDataOf(nodes)?.trigger_type ?? SIGNAL_TRIGGER],
  ]);
  const queue = [TRIGGER_ID];
  while (queue.length) {
    const id = queue.shift()!;
    const node = byId.get(id);
    const below = (node && CHILD_SUBJECT[node.data.kind]) ?? subjects.get(id)!;
    for (const child of outgoing.get(id) ?? []) {
      if (subjects.has(child)) continue;
      subjects.set(child, below);
      queue.push(child);
    }
  }
  return subjects;
}

/** The kind of subject the steps chained under `node` run on. */
export function childSubjectOf(
  node: StepNode,
  subjects: Map<string, WorkflowSubjectKind>,
): WorkflowSubjectKind {
  return CHILD_SUBJECT[node.data.kind] ?? subjects.get(node.id) ?? SIGNAL_TRIGGER;
}

/** Whether `data`'s step makes a new subject for the steps under it. */
export function createsSubject(data: StepData | undefined): boolean {
  return !!data && CHILD_SUBJECT[data.kind] !== undefined;
}

/** A step first on its subject: right under the trigger, or under the step that made it. */
export function isFirstOnSubject(parentId: string | undefined, parent: StepData | undefined): boolean {
  return parentId === TRIGGER_ID || createsSubject(parent);
}

/** Which subjects a step type runs on: the step types endpoint's, else the local table. */
export function stepSubjects(kind: ActionKind, stepTypes?: WorkflowStepType[]): WorkflowSubjectKind[] {
  return stepTypes?.find((t) => t.slug === kind)?.subject_kinds ?? STEP_SUBJECTS[kind] ?? [];
}

// `subjectsOf` the editor's graph, for the canvas: a step on another subject
// than the trigger's (the order a step made) says so on its card.
export const SubjectsContext = createContext<Map<string, WorkflowSubjectKind>>(new Map());
